import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useParams, useRouteMatch } from 'react-router-dom';
import Bugsnag from '@bugsnag/js';
import { Box, Flex } from '@chakra-ui/react';
import userflow from 'userflow.js';

import AlertModal from '../components/AlertModal';
import Header from '../components/Header';
import CurrentUser from '../components/Header/CurrentUser';
import UserCompaniesDropdown from '../components/Header/UserCompaniesDropdown';
import UserSettingsButton from '../components/Header/UserSettingsButton';
import MaintenanceBanner from '../components/MaintenanceBanner';
import Modal from '../components/Modal';
import NavBar from '../components/NavBar';
import LoadingSpinner from '../components/Spinner';
import FormTray from '../components/Tray';
import { COMPANY_INACTIVE_USER, PROCORE_VIEWER } from '../constants/roles';
import { userflowConstants } from '../constants/userflowConstants';
import { PinsAdmin } from '../constants/userLevels';
import AdminPage from '../pages/AdminPage';
import DeactivatedUserPage from '../pages/DeactivatedUserPage';
import DepartmentListPage from '../pages/DepartmentListPage';
import EmailEditorPage from '../pages/EmailEditorPage';
import InsuredsPage from '../pages/InsuredsPage';
import JobDetailsPage from '../pages/JobDetailsPage';
import JobsPage from '../pages/JobsPage';
import NoMatchPage from '../pages/NoMatchPage';
import ReportsPage from '../pages/ReportsPage';
import RequirementTemplateBuilderPage from '../pages/RequirementTemplateBuilderPage';
import TemplatesListPage from '../pages/TemplatesListPage';
import UpdateRequirementsPage from '../pages/UpdateRequirementsPage';
import UserSettingsPage from '../pages/UserSettingsPage';
import V2InsuredProfilePage from '../pages/V2InsuredProfilePage';
import { appMounted } from '../redux/modules/app';
import { getCompany, selectCompany } from '../redux/modules/company';
import { selectNavExpanded, toggleNavBar } from '../redux/modules/navbar';
import { getCurrentUser, getUserCompanies, selectUser } from '../redux/modules/user';
import { useCompanyData } from '../services/company-service';
import { stringFromArray } from '../utils/arrayUtil';
import { getHomePageLink } from '../utils/linkUtil';

const Routes = () => {
  const dispatch = useDispatch();
  const isNavExpanded = useSelector(selectNavExpanded);
  const user = useSelector(selectUser);
  const company = useSelector(selectCompany);
  const { path } = useRouteMatch();
  const { companyId } = useParams();
  const currentUser = useSelector(selectUser);
  const logoLinkHref = getHomePageLink(currentUser?.userLevel, companyId);
  const [userInactive, setUserInactive] = useState(false);
  const [userProcoreViewer, setUserProcoreViewer] = useState(false);
  const [userAnalyzed, setUserAnalyzed] = useState(false);

  // fetch company data for react query
  useCompanyData(companyId);

  useEffect(() => {
    dispatch(appMounted());

    Promise
      .all([
        dispatch(getCurrentUser()),
        dispatch(getCompany(companyId)),
        dispatch(getUserCompanies())
      ])
      .catch(error => {
        window.location.href = '/logout';
        Bugsnag.notify(error);
      });
  }, [companyId, dispatch]);

  useEffect(() => {
    if (user?.userId && companyId) {
      if (user?.userLevel === PinsAdmin) {
        setUserAnalyzed(true);
        return;
      }

      const inactiveUserRole = user.roles.find(role => {
        const userIsInactive = role.name === COMPANY_INACTIVE_USER;

        const pivotIsForCompany = role.pivot.scopeId === parseInt(companyId, 10) && role.pivot.scopeType === 'company';

        return userIsInactive && pivotIsForCompany;
      });

      const companyRoleNames = user.roles
        .filter(role => role.pivot.scopeId === parseInt(companyId, 10) && role.pivot.scopeType === 'company')
        .map(role => role.name);

      const userIsProcoreViewer = companyRoleNames.length === 1 && companyRoleNames[0] === PROCORE_VIEWER;

      if (inactiveUserRole) {
        setUserInactive(true);
      }

      if (userIsProcoreViewer) {
        setUserProcoreViewer(true);
      }

      setUserAnalyzed(true);
    }

    if (user?.userId && company?.companyId && (PINS_ENV === 'qa' || PINS_ENV === 'prod')) {
      userflow.init(userflowConstants.token);
      userflow.identify(user.userId, {
        user_id: user.userId,
        name: `${user.userFirstName} ${user.userLastName}`,
        email: user.userEmail,
        userLevel: user.userLevel,
        userRoles: stringFromArray(user.roles, ', ', 'displayName'),
        pinsVersion: { set_once: '2.0' },
        last_signed_in_at: new Date().toISOString() // ISO 8601 format
      });

      userflow.group(
        company.companyId,
        {
          group_id: company.companyId,
          name: company.companyName,
          pinsENV: PINS_ENV
        }
      );
    }
  }, [company, user]);

  const renderMainContent = () => {
    if (!company?.error && (!company.companyIsLoaded || !user || !userAnalyzed)) return <LoadingSpinner />;

    if (userInactive) {
      return <DeactivatedUserPage companyName={company.companyName} />;
    }

    if (userProcoreViewer) {
      window.location.href = '/logout';

      return (<div>Procore Viewer Logging out, redirecting to login page</div>);
    }

    return (
      <>
        <Switch>
          <Route
            component={DepartmentListPage}
            path={`${path}/departments`}
          />
          <Route
            component={AdminPage}
            path={`${path}/admin/:activeTab?`}
          />
          <Route
            component={UserSettingsPage}
            path={`${path}/user-settings/:activeTab?`}
          />
          <Route
            exact
            component={InsuredsPage}
            path={`${path}/insureds`}
          />
          <Route
            exact
            component={UpdateRequirementsPage}
            path={`${path}/insureds/:insuredId/update-requirements/:requirementId`}
          />
          <Route
            component={V2InsuredProfilePage}
            path={`${path}/insureds/:insuredId/:resource?/:resourceId?`}
          />
          <Route
            exact
            component={JobsPage}
            path={`${path}/projects`}
          />
          <Route
            exact
            component={JobDetailsPage}
            path={`${path}/projects/:jobId`}
          />
          <Route
            exact
            component={ReportsPage}
            path={`${path}/reports`}
          />
          <Route
            exact
            component={EmailEditorPage}
            path={`${path}/draft-email`}
          />
          <Route
            exact
            component={TemplatesListPage}
            path={`${path}/templates/:tabId/:activeTab?`}
          />
          <Route
            exact
            component={RequirementTemplateBuilderPage}
            path={`${path}/create-templates/:templateType/:selectedTemplateId`}
          />
          <Route
            component={NoMatchPage}
            path={`${path}/*`}
          />
        </Switch>
        <Modal />
        <FormTray />
        <AlertModal />
      </>
    );
  };

  return (
    <Box>
      <Header
        handleNavClick={() => dispatch(toggleNavBar())}
        isNavOpen={isNavExpanded}
        logoLinkHref={logoLinkHref}
      >
        <UserSettingsButton />
        <CurrentUser />
        <UserCompaniesDropdown />
      </Header>
      <MaintenanceBanner />
      <Flex width="100%">
        <NavBar />
        <Box flexBasis="100%" overflow="auto">
          {renderMainContent()}
        </Box>
      </Flex>
    </Box>
  );
};

export default Routes;
